import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import prepareDocuments from 'helpers/prepareDocuments';
import { IDocument, IFolder, DocumentsType, IDocumentInfo, IWithWebkitPath, DocumentFilters } from 'interfaces';
import { DocumentUpload, ViewType } from 'interfaces/IDocument';
import React from 'react';
import { fetchAvailableDocuments } from 'store/reducers/documentsCreator';

export type choosenDocument = {
  permissions: string[];
  key: React.Key;
  extension?: string;
  size?: number;
  documents_amount?: number;
  subfolders?: IFolder[];
  folder_id?: string;
  parent_folder_id?: string;
  id: string;
};

// const defaultViewType = localStorage.getItem('viewType') as ViewType;

interface DataState {
  documents: IDocument[];
  folders: IFolder[];
  isLoadingDocuments: boolean;
  errorDocuments: string;
  choosenDocuments: choosenDocument[];
  dataDocumentTable: (IDocument | IFolder)[];
  selectedDocument: IDocument | null;
  selectedDocumentsIds: string[];
  selectedRowKeys: React.Key[];
  nestedDocuments: IDocument[];
  nestedFolders: IFolder[];
  selectedVersion: IDocument | null;

  selectedFolder: IFolder | null;
  folderFilesCount: number | null;

  currentFolder: IFolder[];
  viewType: ViewType;
  entityOfContextMenu: IDocument | IFolder | null;
  documentsType: DocumentsType | null;
  emptyText: string;
  isSearcingDocuments: boolean;
  // lastViewedDocument: string;

  fileList: IWithWebkitPath[];
  isAddedViaDragger: boolean;

  filesForDownloading: IDocumentInfo[];
  uploadingDocuments: DocumentUpload[];
  uploadAbortController: AbortController | null;
  uploadingFolders: IFolder[];
  canceledUploads: string[];

  documentIdsInAsyncProcessing: string[];
  documentTimestampsCreating: number[];
  lastMovedEvent: { targetFolderId: string | null } | null;

  expandedFolderIds: string[];
  expandedFoldersCache: Record<string, (IDocument | IFolder)[]>;

  appliedFilters: DocumentFilters;
  statusFilterOptions: string[];
  highlightedDocumentId: string | null;
}

const initialState: DataState = {
  documents: [],
  folders: [],
  isLoadingDocuments: false,
  errorDocuments: '',
  choosenDocuments: [],
  dataDocumentTable: [],
  selectedDocument: null,
  selectedDocumentsIds: [],
  selectedRowKeys: [],
  nestedDocuments: [],
  nestedFolders: [],
  selectedVersion: null,

  selectedFolder: null,
  folderFilesCount: null,
  
  currentFolder: [],
  viewType: 'Table',
  entityOfContextMenu: null,
  documentsType: null,
  emptyText: 'fallback',
  isSearcingDocuments: false,
  // lastViewedDocument: '',

  fileList: [],
  isAddedViaDragger: false,

  filesForDownloading: [],
  uploadingDocuments: [],
  uploadAbortController: null,
  uploadingFolders: [],
  canceledUploads: [],

  documentIdsInAsyncProcessing: [],
  documentTimestampsCreating: [0],
  lastMovedEvent: null,

  expandedFolderIds: [],
  expandedFoldersCache: {},

  appliedFilters: {
    name: null,
    status: [],
    dateFrom: null,
    dateTo: null,
  },
  statusFilterOptions: [],
  highlightedDocumentId: null,
};

export const dataDocumentsSlice = createSlice({
  name: 'datadocuments',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(
      fetchAvailableDocuments.fulfilled,
      (
        state,
        action: PayloadAction<{
          documents: IDocument[];
          dataForDocumentTable: (IDocument | IFolder)[];
          folders: IFolder[];
        }>
      ) => {
        state.errorDocuments = '';
        state.dataDocumentTable = action.payload.dataForDocumentTable;
        state.folders = action.payload.folders;
        state.documents = action.payload.documents;
        state.isLoadingDocuments = false;
      }
    );
    builder.addCase(fetchAvailableDocuments.pending, (state) => {
      state.isLoadingDocuments = true;
      state.dataDocumentTable = [];
      state.folders = [];
      state.documents = [];
      state.nestedDocuments = [];
      state.nestedFolders = [];
    });
    builder.addCase(fetchAvailableDocuments.rejected, (state, action: any) => {
      if (action?.error?.message !== 'Aborted') {
        state.isLoadingDocuments = false;
        state.errorDocuments = action.payload;
        state.documents = [];
        state.folders = [];
        state.dataDocumentTable = [];
        state.nestedDocuments = [];
        state.nestedFolders = [];
      }
    });
  },
  reducers: {
    setDocuments(
      state,
      action: PayloadAction<{ documents: IDocument[]; folders?: IFolder[], activeMenu?: IDocument | IFolder | null }>
    ) {
      state.documents = action.payload.documents;
      state.folders = action.payload.folders || [];
      state.dataDocumentTable = prepareDocuments(
        action.payload.documents,
        action.payload.folders || [],
        action.payload.activeMenu
      );
    },
    addNestedDocumentsAndFolders(
      state,
      action: PayloadAction<{ documents: IDocument[]; folders: IFolder[] }>
    ) {
      const existingDocIds = new Set(state.nestedDocuments.map((d) => d.id));
      const existingFolderIds = new Set(state.nestedFolders.map((f) => f.id));

      const newDocs = action.payload.documents.filter((d) => !existingDocIds.has(d.id));
      const newFolders = action.payload.folders.filter((f) => !existingFolderIds.has(f.id));

      state.nestedDocuments = [...state.nestedDocuments, ...newDocs];
      state.nestedFolders = [...state.nestedFolders, ...newFolders];
    },
    setNestedDocumentsAndFolders(
      state,
      action: PayloadAction<{ documents: IDocument[]; folders: IFolder[] }>
    ) {
      state.nestedDocuments = action.payload.documents;
      state.nestedFolders = action.payload.folders;
    },

    setIsLoadingDocuments(state, action: PayloadAction<boolean>) {
      state.isLoadingDocuments = action.payload;
    },

    setSelectedDocument(state, action: PayloadAction<IDocument | null>) {
      state.selectedDocument = action.payload;
      // if (action.payload) state.lastViewedDocument = action.payload.id;
      // else state.lastViewedDocument = '';
    },

    setSelectedVersion(state, action: PayloadAction<IDocument | null>) {
      state.selectedVersion = action.payload;
    },

    setSelectedDocumentsIds(state, action: PayloadAction<string[]>) {
      state.selectedDocumentsIds = action.payload;
    },
    setChoosenDocuments(state, action: PayloadAction<choosenDocument[]>) {
      state.choosenDocuments = action.payload;
    },
    setSelectedRowKeys(state, action: PayloadAction<React.Key[]>) {
      state.selectedRowKeys = action.payload;
    },
    resetRowSelect(state) {
      state.selectedDocumentsIds = [];
      state.choosenDocuments = [];
      state.selectedRowKeys = [];
    },

    setSelectedFolder(state, action: PayloadAction<IFolder | IDocument | null>) {
      state.selectedFolder = action.payload;
    },

    setFolderFilesCount(state, action: PayloadAction<number | null>) {
      state.folderFilesCount = action.payload;
    },

    setDataDocumentTable(state, action: PayloadAction<(IFolder | IDocument)[]>) {
      state.dataDocumentTable = action.payload;
    },

    setCurrentFolder(state, action: PayloadAction<IFolder[]>) {
      state.currentFolder = action.payload;
      // state.lastViewedDocument = '';
      state.expandedFolderIds = [];
      state.expandedFoldersCache = {};
    },

    addToFileList(state, action: PayloadAction<IWithWebkitPath[]>) {
      state.fileList = [...state.fileList, ...action.payload];
    },
    removeFileFromList(state, action: PayloadAction<string[]>) {
      state.fileList = state.fileList.filter(el => !action.payload.includes(el.uid));
    },
    cleanFileList(state) {
      state.fileList = [];
    },

    setIsAddedViaDragger(state, action: PayloadAction<boolean>) {
      state.isAddedViaDragger = action.payload;
    },

    setViewType(state, action: PayloadAction<ViewType>) {
      state.viewType = action.payload;

      if (action.payload === 'Document') {
        state.selectedDocumentsIds = [];
        state.choosenDocuments = [];
        state.selectedRowKeys = [];
      };
    },
    setEntityOfContextMenu(
      state,
      action: PayloadAction<IDocument | IFolder | null>
    ) {
      state.entityOfContextMenu = action.payload;
    },
    setDocumentsType(state, action: PayloadAction<DocumentsType | null>) {
      state.documentsType = action.payload;
    },
    setEmptyText(state, action: PayloadAction<string>) {
      state.emptyText = action.payload;
    },
    setIsSearcingDocuments(state, action: PayloadAction<boolean>) {
      state.isSearcingDocuments = action.payload;
    },
    addFileForDownloading(state, action: PayloadAction<IDocumentInfo>) {
      state.filesForDownloading = [...state.filesForDownloading, action.payload];
    },
    removeDownloadedFile(state, action: PayloadAction<string>) {
      state.filesForDownloading = state.filesForDownloading.filter(elem => elem.token !== action.payload);
    },

    setUploadingFolders(state, action: PayloadAction<IFolder & { old_folder_id?: string }>) {
      const { id, old_folder_id } = action.payload;
      
      const existingItemIndex = state.uploadingFolders.findIndex(
        (item) => item.id === id || item.id === old_folder_id
      );
      
      if (existingItemIndex !== -1) {
        state.uploadingFolders[existingItemIndex] = {
          ...state.uploadingFolders[existingItemIndex],
          ...action.payload,
          id,
        };
      } else {
        state.uploadingFolders.push(action.payload);
      }
    },
    clearUploadingFolders(state) {
      state.uploadingFolders = [];
    },

    setUploadingDocuments(state, action: PayloadAction<DocumentUpload>) {
      const { document_id, old_document_id } = action.payload;

      const existingItemIndex = state.uploadingDocuments.findIndex(
        (item) =>
          item.document_id === document_id ||
          item.document_id === old_document_id
      );
      if (existingItemIndex !== -1) {
        state.uploadingDocuments[existingItemIndex] = {
          ...state.uploadingDocuments[existingItemIndex],
          ...action.payload,
          document_id,
        };
      } else {
        state.uploadingDocuments.push(action.payload);
      }
    },

    clearUploadingDocuments(state) {
      state.uploadingDocuments = [];
    },

    setUploadAbortController(
      state,
      action: PayloadAction<AbortController | null>
    ) {
      if (action.payload) {
        state.uploadAbortController = action.payload;
      } else {
        state.uploadAbortController = null;
      }
    },
    
    setCanceledUploads(state, action: PayloadAction<string | string[]>) {
      if (Array.isArray(action.payload)) {
        state.canceledUploads = [...state.canceledUploads, ...action.payload];
      } else {
        state.canceledUploads = [...state.canceledUploads, action.payload];
      }
    },

    addDocumentIdInAsyncProcessing(state, action: PayloadAction<string>) {
      state.documentIdsInAsyncProcessing = [...state.documentIdsInAsyncProcessing, action.payload];
    },
    deleteDocumentIdInAsyncProcessing(state, action: PayloadAction<string>) {
      state.documentIdsInAsyncProcessing = state.documentIdsInAsyncProcessing.filter(id => id !== action.payload);
    },

    setDocumentTimestampsCreating(state, action: PayloadAction<number>) {
      state.documentTimestampsCreating = [...state.documentTimestampsCreating, action.payload];
    },
    deleteDocumentTimestampsCreating(state, action: PayloadAction<number>) {
      state.documentTimestampsCreating = state.documentTimestampsCreating.filter(current => current !== action.payload);
    },
    setLastMovedEvent(state, action: PayloadAction<{ targetFolderId: string | null } | null>) {
      state.lastMovedEvent = action.payload;
    },

    setExpandedFolderIds(state, action: PayloadAction<string[]>) {
      state.expandedFolderIds = action.payload;
    },
    setExpandedFoldersCache(state, action: PayloadAction<Record<string, (IDocument | IFolder)[]>>) {
      state.expandedFoldersCache = action.payload;
    },
    setAppliedFilters(state, action: PayloadAction<Partial<DocumentFilters>>) {
      state.appliedFilters = { ...state.appliedFilters, ...action.payload };
    },
    resetAppliedFilters(state) {
      state.appliedFilters = initialState.appliedFilters;
    },
    setStatusFilterOptions(state, action: PayloadAction<string[]>) {
      state.statusFilterOptions = action.payload;
    },
    setHighlightedDocumentId(state, action: PayloadAction<string | null>) {
      state.highlightedDocumentId = action.payload;
    },
  },
});

const documentReducer = dataDocumentsSlice.reducer;

export const {
  setDocuments,
  addNestedDocumentsAndFolders,
  setNestedDocumentsAndFolders,
  setIsLoadingDocuments,
  setSelectedDocument,
  setSelectedVersion,
  
  setSelectedDocumentsIds,
  setChoosenDocuments,
  setSelectedRowKeys,
  resetRowSelect,

  setSelectedFolder,
  setFolderFilesCount,
  
  setDataDocumentTable,
  setCurrentFolder,

  addToFileList,
  removeFileFromList,
  cleanFileList,

  setIsAddedViaDragger,
  setViewType,
  setEntityOfContextMenu,
  setDocumentsType,
  setEmptyText,
  setIsSearcingDocuments,

  addFileForDownloading,
  removeDownloadedFile,

  setUploadingDocuments,
  clearUploadingDocuments,
  setUploadAbortController,
  setUploadingFolders,
  clearUploadingFolders,
  setCanceledUploads,
  
  addDocumentIdInAsyncProcessing,
  deleteDocumentIdInAsyncProcessing,
  setDocumentTimestampsCreating,
  deleteDocumentTimestampsCreating,
  setLastMovedEvent,
  setExpandedFolderIds,
  setExpandedFoldersCache,
  setAppliedFilters,
  resetAppliedFilters,
  setStatusFilterOptions,
  setHighlightedDocumentId,
} = dataDocumentsSlice.actions;

export default documentReducer;
